import { Breadcrumbs as NextUIBreadcrumbs, BreadcrumbItem } from "@heroui/react";
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { useLocale } from '~/hooks/useLocale';
import { formatHref } from '~/components/atoms/Link/Link';
import { FiArrowLeft } from 'react-icons/fi';

interface BreadcrumbsProps {
  items: { label: string; href?: string }[];
  classNames?: {
    link?: string;
    separator?: string;
  };
}

export const Breadcrumbs = ({ items, classNames }: BreadcrumbsProps) => {
  const { t } = useTranslation();
  const { currentLocale } = useLocale();
  const router = useRouter();

  const handleBack = () => {
    // Check if there's a previous path in sessionStorage
    const prevPath = sessionStorage.getItem('prevPath');

    if (prevPath && prevPath !== router.asPath) {
      // Navigate to the previous path if it exists and is different from current path
      router.push(prevPath);
    } else {
      // Fallback to browser history
      router.back();
    }
  };

  if (items.length === 0) return null;

  const defaultLinkClass = "text-primary-600 hover:text-primary-700";
  const defaultSeparatorClass = "text-foreground/60";

  return (
    <NextUIBreadcrumbs
      size="lg"
      className="p-4"
      onAction={(key) => {
        if (key === 'back') {
          handleBack();
        }
      }}
      classNames={{
        separator: classNames?.separator || defaultSeparatorClass,
      }}
    >
      <BreadcrumbItem key="back" onPress={handleBack}>
        <div className="flex flex-row items-center gap-2 text-primary">
          <FiArrowLeft />
          {t('common.back')}
        </div>
      </BreadcrumbItem>
      {items.map((item, index) => (
        <BreadcrumbItem
          key={index}
          href={formatHref(currentLocale.code, item.href || '')}
          className={classNames?.link || defaultLinkClass}
        >
          {item.label}
        </BreadcrumbItem>
      ))}
    </NextUIBreadcrumbs>
  );
}; 